import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { useAppSettings } from '../context/AppSettingsContext';
import { confirm } from '../components/ui/confirm-dialog';

export default function SessionDetails() {
  const { formatDateTime: fmtDateTime } = useAppSettings();
  const { staffId } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL || '/api';
  const [sessions, setSessions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/users/session/user/${staffId}/`);
      const data = await response.json();
      if (response.ok && data.success) {
        setSessions(data.data.sessions || []);
        setUser(data.data.user || null);
      } else {
        setError(data.error || 'Failed to load sessions');
      }
    } catch (err) {
      setError('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  }, [API, staffId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleStartSession = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API}/users/session/start/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: staffId })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Session started successfully');
        loadSessions();
      } else {
        setError(data.error || 'Failed to start session');
      }
    } catch (err) {
      setError('Failed to start session');
    }
  };

  const handleEndSession = async () => {
    if (!(await confirm({ description: `Are you sure you want to end the current session?`, confirmText: 'End session', tone: 'destructive' }))) return;
    
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API}/users/session/end/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: staffId })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Session ended successfully');
        loadSessions();
      } else {
        setError(data.error || 'Failed to end session');
      }
    } catch (err) {
      setError('Failed to end session');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return fmtDateTime(isoString);
  };

  const activeSession = sessions.find(s => s.is_active);

  return (
    <div className="w-full p-6">
      <div className="mb-4 flex items-center justify-between">
        <Button onClick={() => navigate(`/staff/${staffId}`)} variant="outline">
          ← Back to User Details
        </Button>
        <div className="flex gap-2">
          {activeSession ? (
            <Button onClick={handleEndSession} variant="destructive">
              End Current Session
            </Button>
          ) : (
            <Button onClick={handleStartSession}>
              Start New Session
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
      {success && <Alert className="mb-4 bg-green-100 text-green-800">{success}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>
            Session History - {user?.full_name || 'Loading...'}
            {activeSession && (
              <Badge className="ml-3 bg-green-500">Active Session</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading sessions...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session ID</TableHead>
                    <TableHead>Login Time</TableHead>
                    <TableHead>Logout Time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.session_id} className={session.is_active ? 'bg-green-50' : ''}>
                      <TableCell className="font-medium">{session.session_id}</TableCell>
                      <TableCell>{formatDateTime(session.login_at)}</TableCell>
                      <TableCell>{formatDateTime(session.logout_at)}</TableCell>
                      <TableCell>{formatDuration(session.duration_seconds)}</TableCell>
                      <TableCell>{session.ip_address || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={session.is_active ? 'default' : 'secondary'}>
                          {session.is_active ? 'Active' : 'Ended'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No sessions found. Start a new session to begin tracking.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
